/* DASH-CUSTOM-02 — WIDGET REGISTRY V1
 * ============================================================================
 * Registro DICHIARATIVO dei widget personalizzabili della Dashboard Manager.
 *
 * COSA E': l'unica fonte di verita su quali widget esistono (chiave stabile),
 * quali sezioni di payload ciascuno consuma, se ha un'API propria e quali
 * capability dichiarate ha.
 *
 * COSA NON E' (DASH-CUSTOM-02): non renderizza nulla, non e' importato da
 * DashboardManager.vue, non modifica il layout visibile, non applica alcuna
 * personalizzazione. E' foundation: nessun effetto finche' non verra' usata.
 *
 * REGOLA SULLE CHIAVI (audit §14, Caso C):
 *   `key` e' l'identita PERSISTITA e deve rimanere STABILE per sempre.
 *   `componentKey` e' solo il nome del componente Vue e PUO' cambiare.
 *   Non usare MAI il nome del componente come chiave.
 *
 * ESCLUSIONI (decisioni di prodotto D-03/D-06):
 *   - filters        -> INFRASTRUTTURA (ManagerFilterBar): controllo
 *                       trasversale, governa il perimetro di tutti gli altri.
 *   - InSviluppoCard -> placeholder privo di dati/prop/eventi/API: non e' un
 *                       widget. NON entra nel registry (D-06).
 *   - HeatmapDrillModal / DrillDownDrawer -> overlay, non occupano spazio nel
 *                       flusso: si aprono su richiesta, non sono widget.
 *   - BlockState     -> supporto interno degli stati loading/non disponibile.
 *   - StatePanel / TopClientsCard -> presenti su disco ma NON usati.
 *
 * AUTHORIZATION (STEP 9):
 *   `permissionKey` e' CONCETTUALE. Le righe corrispondenti NON esistono
 *   in SHARED_FUNZIONALITA e NON sono state create: questa fase non introduce
 *   permessi DB. Ogni chiave e' quindi marcata `permissionEnforced: false` e
 *   non va letta come un controllo di sicurezza reale.
 */

/* Stati del ciclo di vita del widget (futuro backend).
 * STEP 9: "volutamente non richiesto" NON e' "fallito" e non e' "non
 * disponibile": sono stati distinti e non sovrapponibili. */
export const WIDGET_STATE = Object.freeze({
    AVAILABLE: 'AVAILABLE',
    NOT_REQUESTED: 'NOT_REQUESTED',
    UNAUTHORIZED: 'UNAUTHORIZED',
    ERROR: 'ERROR',
});

export const DASHBOARD_LAYOUT_SCHEMA_VERSION = 1;

/* DASH-CUSTOM-06B — versione dello schema V2 (order + span).
 * NON e' la versione ATTIVA: finche' il provider PHP e' Stage 05 / schema 1,
 * il runtime continua a usare DASHBOARD_LAYOUT_SCHEMA_VERSION (1). La V2 e' una
 * foundation separata (dashboardLayoutV2.js) che verra' attivata in 06C/06D.
 * Il valore e' centralizzato qui perche' il registry e' la fonte unica delle
 * capability dei widget. */
export const DASHBOARD_LAYOUT_SCHEMA_VERSION_V2 = 2;

const WIDGETS = Object.freeze([
    {
        key: 'welcome',
        componentKey: 'WelcomeCard',
        defaultEnabled: true,
        payloadSections: [],
        hasOwnApi: false,
        draggable: true,
        /* D-02: nella V1 non esiste resize ne' larghezza personalizzabile.
         * futureResizable dichiara solo l'intenzione per la V2. */
        futureResizable: false,
        /* DASH-CUSTOM-06B — CAPABILITY GEOMETRICHE (V2, solo larghezza/span).
         * welcome e' SYSTEM UI: NON e' personalizzabile e NON riceve span.
         * defaultSpan = null => il widget NON e' "span-capable" (nessuna
         * preferenza geometrica utente). Vedi dashboardLayoutV2.js. */
        defaultSpan: null,
        minSpan: null,
        maxSpan: null,
        resizable: false,
        permissionKey: 'DASHBOARD_WIDGET_WELCOME',
        permissionEnforced: false,
    },
    {
        key: 'kpi',
        /* Un solo widget per le 4 card KPI: condividono payload.kpi e la stessa
         * altezza (138px imposta dal parent). Vedi audit §3.3 opzione A. */
        componentKey: 'KpiCard',
        defaultEnabled: true,
        payloadSections: ['kpi'],
        hasOwnApi: false,
        draggable: true,
        futureResizable: false,
        /* DASH-CUSTOM-06F - grid: colonna sx della prima fascia (5/12),
         * 4 card 2x2 sotto Welcome. min 3. */
        defaultSpan: 5,
        minSpan: 3,
        maxSpan: 12,
        resizable: true,
        /* D-04: in V1 KPI e trend NON sono piu' considerati obbligatori per
         * ragioni di prodotto. Restano pero' blocchi CORE per data_status
         * nell'implementazione corrente: la distinzione e' documentata in
         * dashboardLayoutConfig.js e verra' trattata in fase payload. */
        permissionKey: 'DASHBOARD_WIDGET_FINANCE',
        permissionEnforced: false,
    },
    {
        key: 'project_status',
        componentKey: 'ProjectStatusCard',
        defaultEnabled: true,
        payloadSections: ['stati', 'progetti'],
        hasOwnApi: false,
        draggable: true,
        futureResizable: true,
        /* V2 geometry: col-6 dentro la colonna dx (col-xl-7) => ~3.5/12, arrot.
         * a 4. min 3. */
        defaultSpan: 4,
        minSpan: 3,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_PROJECTS',
        permissionEnforced: false,
    },
    {
        key: 'activity',
        componentKey: 'ActivityPanel',
        defaultEnabled: true,
        payloadSections: [],
        /* API PROPRIA, fuori dal payload dashboard: shared/chat/messaggi-non-letti.
         * D-03: resta INDIPENDENTE dai filtri; non modificare la sua API. */
        hasOwnApi: true,
        draggable: true,
        futureResizable: true,
        /* DASH-CUSTOM-06F - grid: meta' destra del blocco 7/12 (3/12),
         * affiancata a project_status (4/12). min 3. */
        defaultSpan: 3,
        minSpan: 3,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_ACTIVITY',
        permissionEnforced: false,
    },
    {
        key: 'trend',
        componentKey: 'TrendChart',
        defaultEnabled: true,
        payloadSections: ['trend'],
        hasOwnApi: false,
        draggable: true,
        /* ECharts: resize in altura vietato (audit §11). TrendChart e' congelato
         * e protetto da hash SHA-256 nella suite di test. */
        futureResizable: false,
        /* DASH-CUSTOM-06F - grid: fascia 2, 7/12 (affiancato a in_sviluppo).
         * Chart ECharts: min 6. */
        defaultSpan: 7,
        minSpan: 6,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_FINANCE',
        permissionEnforced: false,
    },
    {
        key: 'in_sviluppo',
        /* DASH-CUSTOM-06F - widget indipendente (ex placeholder annidato in
         * trend). Nessun dato inventato: resta un placeholder UI-06. */
        componentKey: 'InSviluppoCard',
        defaultEnabled: true,
        payloadSections: [],
        hasOwnApi: false,
        draggable: true,
        futureResizable: false,
        /* DASH-CUSTOM-06F - grid: fascia 2, 5/12 (affiancato a trend). min 3. */
        defaultSpan: 5,
        minSpan: 3,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_FINANCE',
        permissionEnforced: false,
    },
    {
        key: 'heatmap',
        componentKey: 'HeatmapGrid',
        defaultEnabled: true,
        payloadSections: ['heatmap'],
        hasOwnApi: false,
        draggable: true,
        /* Griglia interna 6x2 (col-6 col-md-4 col-xl-2): sotto una certa
         * larghezza le celle diventano illeggibili. */
        futureResizable: false,
        /* V2 geometry: col-12. La griglia interna e' 6x2: min 6 (sotto le celle
         * diventano illeggibili). */
        defaultSpan: 12,
        minSpan: 6,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_PROJECTS',
        permissionEnforced: false,
    },
    {
        key: 'rankings',
        componentKey: 'RankingsPanel',
        defaultEnabled: true,
        payloadSections: ['rankings'],
        hasOwnApi: false,
        draggable: true,
        futureResizable: true,
        /* DASH-CUSTOM-06F - grid: fascia 4, 7/12 (affiancato a supplier_spend).
         * Tabella/classifica: min 4. */
        defaultSpan: 7,
        minSpan: 4,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_RANKINGS',
        permissionEnforced: false,
    },
    {
        key: 'supplier_spend',
        componentKey: 'SupplierSpendCard',
        defaultEnabled: true,
        payloadSections: ['spese_fornitore'],
        hasOwnApi: false,
        draggable: true,
        /* Secondo ECharts: resize in altura vietato. */
        futureResizable: false,
        /* DASH-CUSTOM-06F - grid: fascia 4, 5/12 (affiancato a rankings).
         * Chart ECharts: min 4. */
        defaultSpan: 5,
        minSpan: 4,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_SUPPLIERS',
        permissionEnforced: false,
    },
    {
        key: 'recent',
        componentKey: 'RecentPanel',
        defaultEnabled: true,
        payloadSections: ['recent'],
        hasOwnApi: false,
        draggable: true,
        futureResizable: true,
        /* V2 geometry: col-12. Tabella recenti: min 6. */
        defaultSpan: 12,
        minSpan: 6,
        maxSpan: 12,
        resizable: true,
        permissionKey: 'DASHBOARD_WIDGET_RECENT',
        permissionEnforced: false,
    },
]);

const BY_KEY = Object.freeze(
    WIDGETS.reduce((acc, w) => {
        acc[w.key] = w;
        return acc;
    }, Object.create(null))
);

export const WIDGET_REGISTRY_V1 = Object.freeze({
    schemaVersion: DASHBOARD_LAYOUT_SCHEMA_VERSION,
    widgets: WIDGETS,
    keys() {
        return WIDGETS.map((w) => w.key);
    },
    get(key) {
        return Object.prototype.hasOwnProperty.call(BY_KEY, key) ? BY_KEY[key] : undefined;
    },
    has(key) {
        return Object.prototype.hasOwnProperty.call(BY_KEY, key);
    },
    /* true se il widget consuma almeno una sezione del payload dashboard. */
    usesPayload(key) {
        const w = BY_KEY[key];
        return !!(w && w.payloadSections && w.payloadSections.length > 0);
    },
    /* DASH-CUSTOM-06B — CAPABILITY GEOMETRICHE V2.
     * `geometry` restituisce i limiti dichiarati (o null se la chiave non
     * esiste). `spanCapable` e' true quando il widget possiede uno span
     * personalizzabile (defaultSpan finito). welcome NON lo e' (SYSTEM UI):
     * resta nel contratto per compatibilita' ma senza preferenza geometrica. */
    geometry(key) {
        const w = BY_KEY[key];
        if (!w) return null;
        return {
            defaultSpan: w.defaultSpan === undefined ? null : w.defaultSpan,
            minSpan: w.minSpan === undefined ? null : w.minSpan,
            maxSpan: w.maxSpan === undefined ? null : w.maxSpan,
            resizable: w.resizable === true,
        };
    },
    spanCapable(key) {
        const w = BY_KEY[key];
        return !!(w && typeof w.defaultSpan === 'number' && Number.isFinite(w.defaultSpan));
    },
    spanCapableKeys() {
        return WIDGETS
            .filter((w) => typeof w.defaultSpan === 'number' && Number.isFinite(w.defaultSpan))
            .map((w) => w.key);
    },
});

export default WIDGET_REGISTRY_V1;